import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA,MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { LoggerService } from '@app/logger/logger.service';
import { StockEquipmentsService } from '@app/shared/services/entities/stockEquipments.service';
import {DocumentsUploadDownloadFormComponent } from '@app/inventoryEquipments/documents-upload-download-form/documents-upload-download-form.component';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@app/material.module';
const log = new LoggerService('MAINTENANCE DONE FORM COMPONENT');

interface MaintenanceFile {
  contract: string;
  date: string;
  type: string; // Agregado el tipo de mantenimiento
  status: boolean;
  fileNumber: string;
  pdf: boolean;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
  ],
  selector: 'app-maintenanceDone-form',
  templateUrl: './maintenanceDone-form.component.html',
  styleUrls: ['./maintenanceDone-form.component.scss']
})
export class MaintenanceDoneFormComponent implements OnInit {

  public formGroup: FormGroup;
  public activeContractList = null;
  public contractList = null;
  public contractObj = null;
  public record = null;
  public preventiveMaintenanceSchedule: any[] = [];
  public preventiveMaintenanceFiles: MaintenanceFile[] = [];

  constructor(
    public dialog:MatDialog,
    public dialogRef: MatDialogRef<MaintenanceDoneFormComponent>,
    private customMessageService: CustomMessageService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private inventoryEquipmentsService: StockEquipmentsService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    
    this.activeContractList = this.data.contract.filter(element => element.status === true) || [];
    this.contractList = this.data.contract;
    this.contractObj = this.data.contractObj;
    this.record = this.data.record;
    this.preventiveMaintenanceSchedule = this.data.record.preventiveMaintenanceSchedule || [];
    this.preventiveMaintenanceFiles = this.data.record.preventiveMaintenanceFiles || [];

    // Inicializar los archivos de mantenimiento
    this.initializeMaintenanceFiles();

    this.formGroup = this.formBuilder.group({
      warehouse: [this.data.warehouse ?? this.data.record?.warehouse ?? null],
      type: [this.record ? this.record.type : this.data.equipmentType, Validators.required]
    });

    log.debug('Datos iniciales:', {
      preventiveMaintenanceSchedule: this.preventiveMaintenanceSchedule,
      preventiveMaintenanceFiles: this.preventiveMaintenanceFiles
    });
  }

  /**@description Inicializar archivos de mantenimiento basado en la programación */
  private initializeMaintenanceFiles() {
    if (this.preventiveMaintenanceFiles.length === 0) {
      // Si no hay archivos, crear desde la programación
      this.preventiveMaintenanceFiles = this.preventiveMaintenanceSchedule.map(schedule => ({
        contract: schedule.contract,
        date: schedule.date,
        type: schedule.type || 'preventivo', // Incluir el tipo de mantenimiento con valor por defecto
        status: false, // Por defecto pendiente
        fileNumber: '', // Vacío inicialmente
        pdf: false
      }));
      log.debug('Archivos inicializados desde programación:', this.preventiveMaintenanceFiles);
    } else {
      // Si ya hay archivos, revisar si hay fechas nuevas en la programación
      const existingKeys = this.preventiveMaintenanceFiles.map(file => `${file.contract}-${file.date}-${file.type || 'preventivo'}`);
      
      this.preventiveMaintenanceSchedule.forEach(schedule => {
        const key = `${schedule.contract}-${schedule.date}-${schedule.type || 'preventivo'}`;
        if (!existingKeys.includes(key)) {
          // Agregar nueva fecha
          this.preventiveMaintenanceFiles.push({
            contract: schedule.contract,
            date: schedule.date,
            type: schedule.type || 'preventivo',
            status: false,
            fileNumber: '',
            pdf:false
          });
          log.debug('Nueva fecha agregada:', schedule);
        }
      });

      // Actualizar archivos existentes que no tengan tipo o tengan valor undefined/null
      this.preventiveMaintenanceFiles.forEach(file => {
        if (!file.type) {
          const scheduleMatch = this.preventiveMaintenanceSchedule.find(
            schedule => schedule.contract === file.contract && schedule.date === file.date
          );
          file.type = scheduleMatch?.type || 'preventivo';
        }
      });

      // Ordenar por fecha
      this.preventiveMaintenanceFiles.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }
  }

  /**@description Obtener archivos de mantenimiento con información del contrato */
  getMaintenanceFilesWithContracts() {
    return this.preventiveMaintenanceFiles
      .map(file => ({
        ...file,
        contractName: this.contractObj[file.contract] || 'Contrato no encontrado'
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }

  /**@description Cambiar el estado de un servicio */
  toggleServiceStatus(fileItem: MaintenanceFile) {
    const index = this.preventiveMaintenanceFiles.findIndex(
      file => file.contract === fileItem.contract && 
              file.date === fileItem.date && 
              (file.type || 'preventivo') === (fileItem.type || 'preventivo')
    );
    
    if (index !== -1) {
      this.preventiveMaintenanceFiles[index].status = !this.preventiveMaintenanceFiles[index].status;
      
      // Si se marca como pendiente, limpiar el número de folio
      if (!this.preventiveMaintenanceFiles[index].status) {
        this.preventiveMaintenanceFiles[index].fileNumber = '';
      }
      
      this.cdr.detectChanges();
      log.debug('Estado cambiado:', this.preventiveMaintenanceFiles[index]);
    }
  }

  /**@description Actualizar número de folio */
  updateFileNumber(fileItem: MaintenanceFile, event: Event) {
    const target = event.target as HTMLInputElement;
    const newFileNumber = target.value.trim();
    
    const index = this.preventiveMaintenanceFiles.findIndex(
      file => file.contract === fileItem.contract && 
              file.date === fileItem.date && 
              (file.type || 'preventivo') === (fileItem.type || 'preventivo')
    );
    
    if (index !== -1) {
      this.preventiveMaintenanceFiles[index].fileNumber = newFileNumber;
      log.debug('Número de folio actualizado:', this.preventiveMaintenanceFiles[index]);
    }
  }

  /**@description Obtener el estado como texto */
  getStatusText(status: boolean): string {
    return status ? 'Realizado' : 'Pendiente';
  }

  /**@description Obtener la clase CSS del estado */
  getStatusClass(status: boolean): string {
    return status ? 'status-completed' : 'status-pending';
  }

  /**@description Obtener el texto del tipo de mantenimiento */
  getMaintenanceTypeText(type: string): string {
    if (!type) {
      return 'Preventivo'; // Valor por defecto
    }
    
    const types: { [key: string]: string } = {
      'preventivo': 'Preventivo',
      'correctivo': 'Correctivo',
      'predictivo': 'Predictivo',
      'emergencia': 'Emergencia'
    };
    
    const normalizedType = type.toLowerCase().trim();
    return types[normalizedType] || (type.charAt(0).toUpperCase() + type.slice(1));
  }

  /**@description Obtener la clase CSS del tipo de mantenimiento */
  getMaintenanceTypeClass(type: string): string {
    if (!type) {
      return 'maintenance-preventive'; // Clase por defecto
    }
    
    const typeClasses: { [key: string]: string } = {
      'preventivo': 'maintenance-preventive',
      'correctivo': 'maintenance-corrective',
      'predictivo': 'maintenance-predictive',
      'emergencia': 'maintenance-emergency'
    };
    
    const normalizedType = type.toLowerCase().trim();
    return typeClasses[normalizedType] || 'maintenance-default';
  }

  /**@description Obtener el ícono del tipo de mantenimiento */
  getMaintenanceTypeIcon(type: string): string {
    if (!type) {
      return 'shield'; // Ícono por defecto
    }
    
    const typeIcons: { [key: string]: string } = {
      'preventivo': 'shield',
      'correctivo': 'build',
      'predictivo': 'analytics',
      'emergencia': 'warning'
    };
    
    const normalizedType = type.toLowerCase().trim();
    return typeIcons[normalizedType] || 'settings';
  }

  /**@description Obtener count de servicios completados */
  getCompletedServicesCount(): number {
    return this.preventiveMaintenanceFiles.filter(file => file.status).length;
  }

  /**@description Obtener count de servicios pendientes */
  getPendingServicesCount(): number {
    return this.preventiveMaintenanceFiles.filter(file => !file.status).length;
  }

  /**@description TrackBy function para mejorar performance del ngFor */
  trackByMaintenanceFile(index: number, item: any): string {
    return `${item.contract}-${item.date}-${item.type || 'preventivo'}`;
  }

  /**@description Validar que los servicios realizados tengan folio */
  private validateCompletedServices(): boolean {
    const completedWithoutFolio = this.preventiveMaintenanceFiles.filter(
      file => file.status && !file.fileNumber.trim()
    );

    if (completedWithoutFolio.length > 0) {
      this.customMessageService.open({
        title: 'Validación', 
        type: 'warning', 
        message: 'Los servicios marcados como realizados deben tener un número de folio.', 
        dismissBtn: true
      });
      return false;
    }

    return true;
  }

  /**@description Función para actualizar equipamiento */
  async update() {
    try {
      // Validar servicios completados
      // if (!this.validateCompletedServices()) {
      //   return;
      // }

      // if (!this.formGroup.valid) {
      //   await this.customMessageService.open({
      //     title: 'Cuidado', 
      //     type: 'warning', 
      //     message: 'Es necesario completar todos los campos.', 
      //     dismissBtn: true
      //   });
      //   return;
      // }
  
      let data = JSON.parse(JSON.stringify(this.record));
      
      // Actualizar con los archivos de mantenimiento
      data['preventiveMaintenanceFiles'] = this.preventiveMaintenanceFiles;
      data['id'] = this.record['id'];
      data['operationType'] = 'single'
      log.debug('Datos a guardar:', data);

      this.customMessageService.open({
        title: 'Espere', 
        type: 'success', 
        message: 'Guardando servicios realizados', 
        loading: true 
      });
      
      let apiResponse = await this.inventoryEquipmentsService.update(data);
      this.customMessageService.close();
      
      if (apiResponse.state == 'success') {
        await this.customMessageService.open({
          title: '¡Hecho!', 
          type: 'success', 
          message: apiResponse['message'], 
          duration: 2000 
        });
        
        // Incluir los archivos de mantenimiento en la respuesta
        const responseData = {
          ...apiResponse.data,
          preventiveMaintenanceFiles: this.preventiveMaintenanceFiles
        };
        
        this.dialogRef.close(responseData);
      } else {
        await this.customMessageService.open({
          title: 'Error', 
          type: 'error', 
          message: apiResponse['message'], 
          dismissBtn: true 
        });
      }

    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({
        title: 'Error', 
        type: 'error', 
        message: err, 
        dismissBtn: true 
      });
    }
  }

    /**@description Se abre ventana para agregar consumo */
  openUpload(data,type){
    const dialogRef = this.dialog.open(DocumentsUploadDownloadFormComponent, {
      disableClose: true,
      width: '100%',
      data: {
        record: this.record,
        data: data,
        type: type
      },
    });

  dialogRef.afterClosed().subscribe(async result => {
    if (result && result.pdf) {
      log.debug('result', result);
      
      // Buscar el índice del item en el array original
      const index = this.preventiveMaintenanceFiles.findIndex(
        file => file.contract === data.contract && 
                file.date === data.date && 
                (file.type || 'preventivo') === (data.type || 'preventivo')
      );
      
      if (index !== -1) {
        // Actualizar el valor de pdf en el array original
        this.preventiveMaintenanceFiles[index].pdf = result.pdf;
        
        // También actualizar el objeto data que se pasó
        data.pdf = result.pdf;
        
        // Forzar detección de cambios
        this.cdr.detectChanges();
        
        log.debug('PDF actualizado:', this.preventiveMaintenanceFiles[index]);
        
        this.customMessageService.open({
          title: '¡Hecho!',
          type: 'success',
          message: 'Documento cargado exitosamente',
          duration: 2000
        });
      }
    }
  });
}

  /**@description Se abre ventana para descargar consumo */
  openDownload(data,type){
    const dialogRef = this.dialog.open(DocumentsUploadDownloadFormComponent, {
      disableClose: true,
      width: '100%',
      data: {
        record: this.record,
        data: data,
        type: type
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
    });
  }
}